import { ActionHandler } from "..";
import { clinicalPlanIdPrefix, clinicalPlans } from "@utils/plansMapping";
import {
  clearProductSwitchData,
  CurrentSubscriptionInfo,
  ProductSwitchPreviewOrder,
  setPreviewOrder,
  setProductSwitchData,
} from "src/utils/redux/slices/productSwitch";
import getStore from "src/utils/redux/store";
import {
  ProductSwitchOfferQuery,
  queryProductSwitchOffers,
} from "src/api/productSwitchOffers";
import { captureException } from "@utils/error";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import { setRecommendationContext } from "src/utils/redux/slices/recommendationContext";
import { getMedSKUByProductName } from "src/utils/plans";
import { SaleItemInfo } from "src/utils/redux/slices/checkoutOrders";
import {
  createOrderRequest,
  fetchInvoice,
  fetchPreviewBillingCycles,
} from "src/utils/api/purchase/ordersCheckoutApi";
import { getActiveProgramSubscription } from "src/utils/api/subscriptions";
import { extractBillingCycles, getRefundAmount } from "src/utils/ordersUtils";
import { convertGqlSwitchOperationToProto } from "src/utils/orderConversions";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { getActiveUser } from "@noom/hive-auth";
import { SubscriptionProto } from "@noom/noom-contracts/noom_contracts/billing/subscription/v2/subscription";
import { typedKeys } from "src/utils/typeWrappers";
import {
  MedProductSwitchProductName,
  medProductSwitchProductNames,
} from "src/components/product-switch/types";

function resolveProductName(
  planId?: string
): MedProductSwitchProductName | undefined {
  for (const k of typedKeys(medProductSwitchProductNames)) {
    if (planId?.startsWith(clinicalPlanIdPrefix[k])) return clinicalPlans[k];
  }

  return undefined;
}

async function loadCurrentSubscription(): Promise<
  CurrentSubscriptionInfo | undefined
> {
  const task = trackTask("LoadCurrentSubscription");
  task.start();

  let subscription: SubscriptionProto | undefined;
  try {
    subscription = await getActiveProgramSubscription();
    if (!subscription) {
      throw new Error("No active program subscription found");
    }
  } catch (error) {
    task.failed(error);
    return undefined;
  }

  const currentSubscription: CurrentSubscriptionInfo = {
    subscriptionId: subscription.id,
    productName: resolveProductName(subscription.planId),
  };

  try {
    const [previewResponse, trialInvoice] = await Promise.all([
      fetchPreviewBillingCycles(subscription.foreignId),
      // Trial invoice is non-critical (only used for displaying the current
      // product card). Errors are tracked internally by fetchInvoice.
      subscription.isInTrialPeriod && subscription.currentInvoiceId
        ? fetchInvoice(subscription.currentInvoiceId).catch(() => undefined)
        : undefined,
    ]);

    const billingCycles = previewResponse.billingCycles ?? [];
    const recurringBillingCycle = billingCycles.find((c) => c.isRecurring);

    task.success();
    return {
      ...currentSubscription,
      trialInvoice,
      recurringBillingCycle,
    };
  } catch (error) {
    task.failed(error);
    return currentSubscription;
  }
}

async function createPreviewOrderForOffer(
  offer: ProductSwitchOfferQuery,
  accessCode: string
): Promise<ProductSwitchPreviewOrder> {
  const task = trackTask("ProductSwitchPreviewOrder", {
    productName: offer.productName,
  });
  task.start();
  if (!offer.switchOperation) {
    const err = new Error(`No switchOperation for offer ${offer.productName}`);
    task.failed(err);
    throw err;
  }
  try {
    const switchOperation = convertGqlSwitchOperationToProto(
      offer.switchOperation
    );
    const order = await createOrderRequest(
      { switchOperation },
      accessCode,
      true
    );
    const { initialBillingCycle, recurringBillingCycle } =
      extractBillingCycles(order);
    const refundAmount = getRefundAmount(order);
    task.success();
    return { initialBillingCycle, recurringBillingCycle, refundAmount };
  } catch (error) {
    task.failed(error);
    throw error;
  }
}

export async function loadAndStoreProductSwitchData() {
  const { productSwitch } = getStore().getState();
  if (productSwitch?.loaded) return;

  try {
    const [currentSubscription, offers] = await Promise.all([
      loadCurrentSubscription(),
      queryProductSwitchOffers(),
    ]);

    getStore().dispatch(
      setProductSwitchData({
        currentSubscription,
        previewOrders: {},
        offers,
        loaded: true,
      })
    );

    const eligibleSaleItems: SaleItemInfo[] = offers.reduce<SaleItemInfo[]>(
      (items, offer) => {
        const medSku = getMedSKUByProductName(offer.productName);
        if (medSku && offer.switchOperation) {
          items.push({
            medSku,
            saleItemId: offer.switchOperation.saleItemId,
            trialSaleItemId: offer.switchOperation.trialSaleItemId,
            planName: offer.productName,
          });
        }
        return items;
      },
      []
    );

    getStore().dispatch(setRecommendationContext({ eligibleSaleItems }));
  } catch (error) {
    captureException(error, "load-product-switch-offer-failed");
    getStore().dispatch(clearProductSwitchData());
    getStore().dispatch(setRecommendationContext({ eligibleSaleItems: [] }));
    trackBuyflowEvent("BuyflowActionFailed", {
      errorMessage: `Failed to load product switch data: ${error}`,
    });
  }
}

function isPreviewOrderResolved(entry?: ProductSwitchPreviewOrder): boolean {
  if (!entry) return false;
  return !!entry.recurringBillingCycle || !!entry.error;
}

let previewOrdersLoading = false;

/**
 * Loads preview orders for all product switch offers and stores them in Redux.
 * Each offer resolves independently so prices appear as soon as they're ready.
 * Skips products that already have data or an explicit error. Entries stuck in
 * a loading state (e.g. from a page refresh that interrupted the API call) are
 * retried.
 */
export async function loadPreviewOrders() {
  if (previewOrdersLoading) return;
  previewOrdersLoading = true;
  const store = getStore();
  const { offers, previewOrders = {} } = store.getState().productSwitch;

  const accessCode = getActiveUser()?.accessCode;
  const offersToLoad = offers.filter(
    (offer) => !isPreviewOrderResolved(previewOrders[offer.productName])
  );

  if (offersToLoad.length === 0 || !accessCode) {
    previewOrdersLoading = false;
    return;
  }

  for (const offer of offersToLoad) {
    store.dispatch(
      setPreviewOrder({
        productName: offer.productName,
        previewOrder: { loading: true },
      })
    );
  }

  await Promise.allSettled(
    offersToLoad.map(async (offer) => {
      try {
        const preview = await createPreviewOrderForOffer(offer, accessCode);
        store.dispatch(
          setPreviewOrder({
            productName: offer.productName,
            previewOrder: preview,
          })
        );
      } catch (error) {
        captureException(error);
        store.dispatch(
          setPreviewOrder({
            productName: offer.productName,
            previewOrder: { error: true },
          })
        );
      }
    })
  );
  previewOrdersLoading = false;
}

export const loadProductSwitchData: ActionHandler = async () => {
  await loadAndStoreProductSwitchData();
};
